import './Admin.css';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { createAuthor } from '../../services/authorService';

function CreateAuthor() {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        name: '',
        shortInfo: '',
        birthDate: '',
        birthDateType: 'exact', // 'exact', 'year', 'unknown'
        birthYear: '',
        birthYearBCE: false, // před naším letopočtem
        deathDate: '',
        deathDateType: 'exact', // 'exact', 'year', 'unknown'
        deathYear: '',
        deathYearBCE: false, // před naším letopočtem
        info: ''
    });

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Scroll to top when error is set
    useEffect(() => {
        if (error) {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }, [error]);

    // Helper function to validate dd-mm-yyyy format
    const validateDateFormat = (dateStr) => {
        const regex = /^(\d{2})-(\d{2})-(\d{4})$/;
        const match = dateStr.match(regex);
        if (!match) return false;

        const day = parseInt(match[1], 10);
        const month = parseInt(match[2], 10);
        const year = parseInt(match[3], 10);

        if (month < 1 || month > 12) return false;
        if (day < 1 || day > 31) return false;

        // Check for valid date
        const date = new Date(year, month - 1, day);
        return date.getFullYear() === year &&
               date.getMonth() === month - 1 &&
               date.getDate() === day;
    };

    // Helper function to convert dd-mm-yyyy to yyyy-mm-dd
    const convertToISODate = (dateStr) => {
        const regex = /^(\d{2})-(\d{2})-(\d{4})$/;
        const match = dateStr.match(regex);
        if (!match) return null;

        const day = match[1];
        const month = match[2];
        const year = match[3];

        return `${year}-${month}-${day}`;
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validation
        if (!formData.name.trim()) {
            setError('Jméno autora je povinné');
            return;
        }
        if (!formData.shortInfo.trim()) {
            setError('Krátké info je povinné');
            return;
        }

        // Validate birth date based on type
        if (formData.birthDateType === 'exact' && !formData.birthDate) {
            setError('Datum narození je povinné');
            return;
        }
        if (formData.birthDateType === 'exact' && !validateDateFormat(formData.birthDate)) {
            setError('Datum narození musí být ve formátu dd-mm-yyyy');
            return;
        }
        if (formData.birthDateType === 'year' && !formData.birthYear) {
            setError('Rok narození je povinný');
            return;
        }
        if (formData.birthDateType === 'year' && formData.birthYear < 1) {
            setError('Rok narození musí být kladné číslo');
            return;
        }
        if (formData.birthDateType === 'year' && !formData.birthYearBCE && formData.birthYear > new Date().getFullYear()) {
            setError('Rok narození nesmí být v budoucnosti');
            return;
        }

        // Validate death date based on type
        if (formData.deathDateType === 'exact' && !formData.deathDate) {
            setError('Prosím vyplňte datum úmrtí nebo změňte typ');
            return;
        }
        if (formData.deathDateType === 'exact' && formData.deathDate && !validateDateFormat(formData.deathDate)) {
            setError('Datum úmrtí musí být ve formátu dd-mm-yyyy');
            return;
        }
        if (formData.deathDateType === 'year' && !formData.deathYear) {
            setError('Rok úmrtí je povinný');
            return;
        }
        if (formData.deathDateType === 'year' && formData.deathYear < 1) {
            setError('Rok úmrtí musí být kladné číslo');
            return;
        }
        if (formData.deathDateType === 'year' && !formData.deathYearBCE && formData.deathYear > new Date().getFullYear()) {
            setError('Rok úmrtí nesmí být v budoucnosti');
            return;
        }

        if (!formData.info.trim()) {
            setError('Detailní informace jsou povinné');
            return;
        }

        try {
            setLoading(true);
            setError(null);

            // Prepare birth date based on type
            let birthDateValue = null;
            if (formData.birthDateType === 'exact') {
                birthDateValue = convertToISODate(formData.birthDate);
            } else if (formData.birthDateType === 'year') {
                // If BCE, store as negative year
                birthDateValue = formData.birthYearBCE
                    ? `-${formData.birthYear}`
                    : formData.birthYear.toString();
            }

            // Prepare death date based on type
            let deathDateValue = null;
            if (formData.deathDateType === 'exact' && formData.deathDate) {
                deathDateValue = convertToISODate(formData.deathDate);
            } else if (formData.deathDateType === 'year' && formData.deathYear) {
                // If BCE, store as negative year
                deathDateValue = formData.deathYearBCE
                    ? `-${formData.deathYear}`
                    : formData.deathYear.toString();
            }

            const authorData = {
                name: formData.name.trim(),
                shortInfo: formData.shortInfo.trim(),
                birthDate: birthDateValue,
                info: formData.info.trim(),
                references: []
            };

            // Only include deathDate if it's not unknown
            if (deathDateValue) {
                authorData.deathDate = deathDateValue;
            }

            await createAuthor(authorData);

            // Navigate immediately with success message
            navigate('/admin/manage-authors', {
                state: { message: 'Autor byl úspěšně vytvořen!' }
            });

        } catch (err) {
            setError('Nepodařilo se vytvořit autora. Zkuste to prosím znovu.');
            console.error('Error creating author:', err);
            setLoading(false);
        }
    };

    const handleReset = () => {
        setFormData({
            name: '',
            shortInfo: '',
            birthDate: '',
            birthDateType: 'exact',
            birthYear: '',
            birthYearBCE: false,
            deathDate: '',
            deathDateType: 'exact',
            deathYear: '',
            deathYearBCE: false,
            info: ''
        });
        setError(null);
    };

    const handleBack = () => {
        navigate('/admin');
    };

    return (
        <div className="admin-page">
            <div className="admin-container">
                <button className="back-button" onClick={handleBack}>
                    ← Zpět na dashboard
                </button>

                <h1 className="page-title">Vytvořit autora</h1>

                {error && (
                    <div className="alert alert-error">
                        {error}
                    </div>
                )}

                <form className="author-form" onSubmit={handleSubmit}>
                    <div className="form-group">
                        <label htmlFor="name" className="form-label">
                            Jméno autora <span className="required">*</span>
                        </label>
                        <input
                            type="text"
                            id="name"
                            name="name"
                            className="form-input"
                            value={formData.name}
                            onChange={handleInputChange}
                            placeholder="např. George Orwell"
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="shortInfo" className="form-label">
                            Krátké info <span className="required">*</span>
                        </label>
                        <textarea
                            id="shortInfo"
                            name="shortInfo"
                            className="form-textarea"
                            rows="5"
                            value={formData.shortInfo}
                            onChange={handleInputChange}
                            placeholder="Krátký popis autora pro seznam..."
                            required
                        />
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label className="form-label">
                                Datum narození <span className="required">*</span>
                            </label>
                            <div className="date-type-selector">
                                <label className="radio-label">
                                    <input
                                        type="radio"
                                        name="birthDateType"
                                        value="exact"
                                        checked={formData.birthDateType === 'exact'}
                                        onChange={handleInputChange}
                                    />
                                    Přesné datum
                                </label>
                                <label className="radio-label">
                                    <input
                                        type="radio"
                                        name="birthDateType"
                                        value="year"
                                        checked={formData.birthDateType === 'year'}
                                        onChange={handleInputChange}
                                    />
                                    Pouze rok
                                </label>
                                <label className="radio-label">
                                    <input
                                        type="radio"
                                        name="birthDateType"
                                        value="unknown"
                                        checked={formData.birthDateType === 'unknown'}
                                        onChange={handleInputChange}
                                    />
                                    Neznámé
                                </label>
                            </div>
                            {formData.birthDateType === 'exact' && (
                                <input
                                    type="text"
                                    name="birthDate"
                                    className="form-input"
                                    value={formData.birthDate}
                                    onChange={handleInputChange}
                                    placeholder="dd-mm-yyyy (např. 25-06-1903)"
                                    required
                                />
                            )}
                            {formData.birthDateType === 'year' && (
                                <div className="year-input-wrapper">
                                    <input
                                        type="number"
                                        name="birthYear"
                                        className="form-input"
                                        placeholder="např. 1903 nebo 480"
                                        min="1"
                                        value={formData.birthYear}
                                        onChange={handleInputChange}
                                        required
                                    />
                                    <label className="checkbox-label">
                                        <input
                                            type="checkbox"
                                            name="birthYearBCE"
                                            checked={formData.birthYearBCE}
                                            onChange={(e) => setFormData(prev => ({...prev, birthYearBCE: e.target.checked}))}
                                        />
                                        př. n. l.
                                    </label>
                                </div>
                            )}
                        </div>

                        <div className="form-group">
                            <label className="form-label">
                                Datum úmrtí (volitelné)
                            </label>
                            <div className="date-type-selector">
                                <label className="radio-label">
                                    <input
                                        type="radio"
                                        name="deathDateType"
                                        value="exact"
                                        checked={formData.deathDateType === 'exact'}
                                        onChange={handleInputChange}
                                    />
                                    Přesné datum
                                </label>
                                <label className="radio-label">
                                    <input
                                        type="radio"
                                        name="deathDateType"
                                        value="year"
                                        checked={formData.deathDateType === 'year'}
                                        onChange={handleInputChange}
                                    />
                                    Pouze rok
                                </label>
                                <label className="radio-label">
                                    <input
                                        type="radio"
                                        name="deathDateType"
                                        value="unknown"
                                        checked={formData.deathDateType === 'unknown'}
                                        onChange={handleInputChange}
                                    />
                                    Neznámé/Žije
                                </label>
                            </div>
                            {formData.deathDateType === 'exact' && (
                                <input
                                    type="text"
                                    name="deathDate"
                                    className="form-input"
                                    value={formData.deathDate}
                                    onChange={handleInputChange}
                                    placeholder="dd-mm-yyyy (např. 21-01-1950)"
                                />
                            )}
                            {formData.deathDateType === 'year' && (
                                <div className="year-input-wrapper">
                                    <input
                                        type="number"
                                        name="deathYear"
                                        className="form-input"
                                        placeholder="např. 1950 nebo 399"
                                        min="1"
                                        value={formData.deathYear}
                                        onChange={handleInputChange}
                                    />
                                    <label className="checkbox-label">
                                        <input
                                            type="checkbox"
                                            name="deathYearBCE"
                                            checked={formData.deathYearBCE}
                                            onChange={(e) => setFormData(prev => ({...prev, deathYearBCE: e.target.checked}))}
                                        />
                                        př. n. l.
                                    </label>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="form-group">
                        <label htmlFor="info" className="form-label">
                            Detailní informace <span className="required">*</span>
                        </label>
                        <textarea
                            id="info"
                            name="info"
                            className="form-textarea"
                            rows="15"
                            value={formData.info}
                            onChange={handleInputChange}
                            placeholder="Podrobný životopis a informace o autorovi..."
                            required
                        />
                    </div>

                    <div className="form-actions">
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={handleReset}
                            disabled={loading}
                        >
                            Vymazat formulář
                        </button>
                        <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={loading}
                        >
                            {loading && <span className="btn-spinner"></span>}
                            {loading ? 'Vytváření...' : 'Vytvořit autora'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default CreateAuthor;
